import React, { createContext, useContext, useState, ReactNode } from 'react';

export type UOMType = 'Imperial' | 'Metric';

interface UOMContextType {
  uom: UOMType;
  setUOM: (uom: UOMType) => void;
  getFieldLabel: (
    baseLabel: string,
    fieldType:
      | 'power'
      | 'temperature'
      | 'length'
      | 'area'
      | 'volume'
      | 'weight'
      | 'pressure'
      | 'flow'
      | 'general',
  ) => string;
}

const UOMContext = createContext<UOMContextType | undefined>(undefined);

// Field label mappings for different UOM types
const fieldLabels = {
  Imperial: {
    power: {
      'Total data center IT Load [kW]': 'Total data center IT Load [kW]',
      'Total Available Power (kW)': 'Total Available Power (kW)',
      'Average IT Load per rack [kW]': 'Average IT Load per rack [kW]',
      'Maximum IT load per rack [kW]': 'Maximum IT load per rack [kW]',
      'Total Available Cooling Capacity [kW]':
        'Total Available Cooling Capacity [kW]',
      'Thermal Load per SFN [kW]': 'Thermal Load per SFN [kW]',
      'Total IT Load per Datahall (kW)': 'Total IT Load per Datahall (kW)',
      'Max KW per Rack': 'Max KW per Rack',
      Runtime: 'Runtime [min]',
      'Expected Load Growth': 'Expected Load Growth [kW]',
    },
    temperature: {
      'Supply Temperature':
        'TCS loop temperature (maximum & minimum) rating °F',
      'Return Temperature': 'Tpr (Return Temp) °F',
      'Temperature Rise Preference': 'Temperature Rise Preference [°F]',
      'Tss (Supply Temp)': 'Tss (Supply Temp) °F',
      'Tsr (Return Temp)': 'Tsr (Return Temp) °F',
    },
    length: {
      'Room Length': 'Room Length [ft]',
      'Room Width': 'Room Width [ft]',
      'Rack Depth': 'Depth [in]',
      'Rack Height': 'Height [in]',
      'Rack Width': 'Width [in]',
      'Clear Space Above Racks': 'Clear space required above racks [in]',
      'Ceiling Height': 'Ceiling Height [ft]',
      'Height of raised floor': 'Height of raised floor [in]',
      'Dropped ceiling height': 'Dropped ceiling height [ft]',
      'Plenum height':
        'If dropped ceiling, plenum height (above dropped ceiling) [ft]',
      'Slab to dropped ceiling': 'Slab to dropped ceiling height [ft]',
      'Busway Length': 'Busway Length [ft]',
      'Drop Cord Length': 'Drop Cord Length [ft]',
      'Raised Floor Depth': 'Raised Floor Depth [in]',
      'TCS Loop Pipe Diameter': 'TCS Loop Pipe Diameter [in]',
      'TCS Rack Connection Size': 'TCS Rack Connection Size [in]',
    },
    area: {
      'Room Width': 'Room Width [ft]',
      'Weight Support': 'Weight support of floor (lbs/sqft)',
    },
    volume: {
      'TCS Coolant Flowrate per Rack': 'TCS Coolant Flowrate per Rack [GPM]',
    },
    weight: {
      'Weight Support': 'Weight support of floor (lbs/sqft)',
    },
    pressure: {
      'Maximum Pressure Rating': 'Maximum Pressure Rating [psi]',
      'Rack Pressure Drop': 'Rack Pressure Drop [psi]',
      'External Static Pressure Requirement':
        'External Static Pressure Requirement [psi]',
      'TCS Loop Pressure (Max)': 'TCS loop pressure (maximum) rating (Bar)',
    },
    flow: {
      'Flowrate Requirement': 'Flowrate Requirement [GPM]',
      'TCS Coolant Flowrate per Rack': 'TCS Coolant Flowrate per Rack [GPM]',
    },
    general: {
      'Main nominal Low Voltage LV [V]': 'Main nominal Low Voltage LV [V]',
      'Anticipated IT Load MW': 'Anticipated IT Load MW',
      'Number and type of power inputs per rack':
        'Number and type of power inputs per rack',
      'Battery Runtime Required': 'Battery Runtime Required [min]',
      'Distribution Voltage to Load': 'Distribution Voltage to Load [V]',
      'Busway Amperage': 'Busway Amperage [A]',
      'RPDU per Rack': 'RPDU per Rack',
      'Max KW per Rack': 'Max KW per Rack',
      'Type of UPS': 'Type of UPS',
      'Input Power Phase': 'Input Power Phase',
      Runtime: 'Runtime [min]',
    },
  },
  Metric: {
    power: {
      'Total data center IT Load [kW]': 'Total data center IT Load [kW]',
      'Total Available Power (kW)': 'Total Available Power (kW)',
      'Average IT Load per rack [kW]': 'Average IT Load per rack [kW]',
      'Maximum IT load per rack [kW]': 'Maximum IT load per rack [kW]',
      'Total Available Cooling Capacity [kW]':
        'Total Available Cooling Capacity [kW]',
      'Thermal Load per SFN [kW]': 'Thermal Load per SFN [kW]',
      'Total IT Load per Datahall (kW)': 'Total IT Load per Datahall (kW)',
      'Max KW per Rack': 'Max KW per Rack',
      Runtime: 'Runtime [min]',
      'Expected Load Growth': 'Expected Load Growth [kW]',
    },
    temperature: {
      'Supply Temperature':
        'TCS loop temperature (maximum & minimum) rating °C',
      'Return Temperature': 'Tpr (Return Temp) °C',
      'Temperature Rise Preference': 'Temperature Rise Preference [°C]',
      'Tss (Supply Temp)': 'Tss (Supply Temp) °C',
      'Tsr (Return Temp)': 'Tsr (Return Temp) °C',
    },
    length: {
      'Room Length': 'Room Length [m]',
      'Room Width': 'Room Width [m]',
      'Rack Depth': 'Depth [mm]',
      'Rack Height': 'Height [mm]',
      'Rack Width': 'Width [mm]',
      'Clear Space Above Racks': 'Clear space required above racks [mm]',
      'Ceiling Height': 'Ceiling Height [m]',
      'Height of raised floor': 'Height of raised floor [mm]',
      'Dropped ceiling height': 'Dropped ceiling height [m]',
      'Plenum height':
        'If dropped ceiling, plenum height (above dropped ceiling) [m]',
      'Slab to dropped ceiling': 'Slab to dropped ceiling height [m]',
      'Busway Length': 'Busway Length [m]',
      'Drop Cord Length': 'Drop Cord Length [m]',
      'Raised Floor Depth': 'Raised Floor Depth [mm]',
      'TCS Loop Pipe Diameter': 'TCS Loop Pipe Diameter [mm]',
      'TCS Rack Connection Size': 'TCS Rack Connection Size [mm]',
    },
    area: {
      'Room Width': 'Room Width [m]',
      'Weight Support': 'Weight support of floor (kg/m²)',
    },
    volume: {
      'TCS Coolant Flowrate per Rack': 'TCS Coolant Flowrate per Rack [LPM]',
    },
    weight: {
      'Weight Support': 'Weight support of floor (kg/m²)',
    },
    pressure: {
      'Maximum Pressure Rating': 'Maximum Pressure Rating [bar]',
      'Rack Pressure Drop': 'Rack Pressure Drop [bar]',
      'External Static Pressure Requirement':
        'External Static Pressure Requirement [bar]',
      'TCS Loop Pressure (Max)': 'TCS loop pressure (maximum) rating (Bar)',
    },
    flow: {
      'Flowrate Requirement': 'Flowrate Requirement [LPM]',
      'TCS Coolant Flowrate per Rack': 'TCS Coolant Flowrate per Rack [LPM]',
    },
    general: {
      'Main nominal Low Voltage LV [V]': 'Main nominal Low Voltage LV [V]',
      'Anticipated IT Load MW': 'Anticipated IT Load MW',
      'Number and type of power inputs per rack':
        'Number and type of power inputs per rack',
      'Battery Runtime Required': 'Battery Runtime Required [min]',
      'Distribution Voltage to Load': 'Distribution Voltage to Load [V]',
      'Busway Amperage': 'Busway Amperage [A]',
      'RPDU per Rack': 'RPDU per Rack',
      'Max KW per Rack': 'Max KW per Rack',
      'Type of UPS': 'Type of UPS',
      'Input Power Phase': 'Input Power Phase',
      Runtime: 'Runtime [min]',
    },
  },
};

export const UOMProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [uom, setUOM] = useState<UOMType>('Imperial');

  const getFieldLabel = (
    baseLabel: string,
    fieldType:
      | 'power'
      | 'temperature'
      | 'length'
      | 'area'
      | 'volume'
      | 'weight'
      | 'pressure'
      | 'flow'
      | 'general',
  ): string => {
    const uomLabels = fieldLabels[uom];
    const typeLabels = uomLabels[fieldType];

    // Try to find exact match first
    if (typeLabels && typeLabels[baseLabel]) {
      return typeLabels[baseLabel];
    }

    // Try to find partial match
    for (const [key, value] of Object.entries(typeLabels || {})) {
      if (baseLabel.includes(key) || key.includes(baseLabel)) {
        return value;
      }
    }

    // Return original label if no match found
    return baseLabel;
  };

  return (
    <UOMContext.Provider value={{ uom, setUOM, getFieldLabel }}>
      {children}
    </UOMContext.Provider>
  );
};

export const useUOM = (): UOMContextType => {
  const context = useContext(UOMContext);
  if (context === undefined) {
    throw new Error('useUOM must be used within a UOMProvider');
  }
  return context;
};
